import { site } from '@/data/site';

const head = () => document.head;

const upsertMeta = (attr, key, content) => {
  const selector = `meta[${attr}="${key}"]`;
  let el = head().querySelector(selector);

  if (!content) {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    head().appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let el = head().querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    head().appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  let el = head().querySelector(`script[data-jsonld="${id}"]`);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-jsonld', id);
    head().appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const absolute = (value, fallback) => {
  const target = value || fallback || '';
  if (!target) return '';
  return /^https?:\/\//i.test(target) ? target : `${site.origin}${target}`;
};

const canonicalUrl = (path) => {
  if (!path || path === '/') return `${site.origin}/`;
  return `${site.origin}${path.replace(/\/+$/, '')}`;
};

/**
 * The site-wide RealEstateAgent record. Injected once at boot and never
 * removed, so it is present on every page.
 */
export const organisationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'RealEstateAgent',
  '@id': `${site.origin}/#organisation`,
  name: site.name,
  alternateName: site.shortName,
  url: `${site.origin}/`,
  image: absolute(site.defaultImage),
  logo: absolute(site.defaultImage),
  description: site.defaultDescription,
  telephone: site.phone,
  email: site.email,
  founder: {
    '@type': 'Person',
    name: site.founder,
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: site.address.street,
    addressLocality: site.address.locality,
    addressRegion: site.address.region,
    postalCode: site.address.postalCode,
    addressCountry: site.address.country,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: site.geo.latitude,
    longitude: site.geo.longitude,
  },
  areaServed: site.areaServed.map((name) => ({ '@type': 'Place', name })),
  sameAs: site.social,
});

const breadcrumbJsonLd = (path, label) => {
  if (!path || path === '/' || !label) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${site.origin}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: label,
        item: canonicalUrl(path),
      },
    ],
  };
};

/**
 * Applies every per-page tag. Safe to call on each navigation.
 *
 * @param {object} meta
 * @param {string} [meta.title]        Page title without the site-name suffix
 * @param {string} [meta.description]
 * @param {string} [meta.keywords]
 * @param {string} [meta.path]         Route path, used for canonical and OG url
 * @param {string} [meta.image]        Absolute URL or a path under public/
 * @param {string} [meta.breadcrumb]   Breadcrumb label for this page
 * @param {boolean} [meta.noindex]
 * @param {object} [meta.jsonLd]       Extra page-level structured data
 */
export const applySeo = (meta = {}) => {
  const path = meta.path || '/';
  const url = canonicalUrl(path);
  const title = meta.title ? `${meta.title} | ${site.shortName}` : site.defaultTitle;
  const description = meta.description || site.defaultDescription;
  const image = absolute(meta.image, site.defaultImage);

  document.title = title;
  if (document.documentElement.lang !== site.lang) {
    document.documentElement.lang = site.lang;
  }

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'keywords', meta.keywords || '');
  upsertMeta(
    'name',
    'robots',
    meta.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'
  );
  upsertLink('canonical', url);

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:site_name', site.name);
  upsertMeta('property', 'og:locale', site.locale);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:image:width', '1200');
  upsertMeta('property', 'og:image:height', '630');
  upsertMeta('property', 'og:image:alt', site.name);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);

  setJsonLd('breadcrumb', breadcrumbJsonLd(path, meta.breadcrumb));
  setJsonLd('page', meta.jsonLd || null);
};

/**
 * Injects the organisation record once, at application boot.
 */
export const initSeo = () => {
  setJsonLd('organisation', organisationJsonLd());
};

export default applySeo;
